import styles from './TxProgress.module.css';

import type { FC, ReactNode } from 'react';
import type { TxProgressState } from './useTxProgress';

import classNames from 'classnames';
import React, { useState, useEffect } from 'react';

import { shortenHash } from 'src/utils/dealChain';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

/**
 * What is happening to my transaction, in four visible phases (contracts v3, UI part §2).
 *
 * The panel exists because the alternative — a button that goes quiet for a few seconds — is read
 * as a broken page, and a buyer who reloads or clicks again during an escrow payment is the
 * expensive kind of confused. So every phase is named in the buyer's words, the transaction is
 * linkable the moment it exists, and the panel does not disappear until the deal itself has moved.
 *
 * The one thing it never does is unblock the action early. After thirty seconds it says the wait
 * is longer than usual and that the funds are safe — and keeps waiting, because the poller will
 * bring the truth and a second signature would only cost a second network fee.
 */

/** After this long on a network phase, say so — without offering anything to click. */
const LONG_WAIT_MS = 30_000;

interface TxProgressProps {
  state: TxProgressState | null;
  error?: string | null;
  /** Explorer prefix for the deal's chain; a hash is only linked when there is one. */
  explorerTxUrl?: string;
  onRetry?: () => void;
  /**
   * The way to connect a wallet, when the error is that none is (app#315): the screen's
   * `Connect Wallet` button for the deal's family, which opens the connection window. Shown under
   * the message in place of Retry — a retry with no wallet would only say the same again.
   */
  connect?: ReactNode;
  /**
   * The caller has said "done" in its own words — a dialog's success screen. The last phase,
   * `syncing`, is normally cleared by the *screen* when the deal's fingerprint changes; a dialog
   * holds its own `useTxProgress` and never sees that change, so without this flag its spinner
   * ("updating your deal…") ran under "Payment sent" until Close (app#198). With it the panel
   * keeps only what the success screen lacks: the transaction, linked.
   */
  done?: boolean;
}

const PHASE_TITLE: Record<TxProgressState['phase'], string> = {
  confirm: 'Confirm in your wallet',
  sent: 'Transaction sent — confirming on the network',
  confirmed: 'Confirmed — updating your deal…',
  syncing: 'Confirmed — updating your deal…',
};

const PHASE_NOTE: Record<TxProgressState['phase'], string> = {
  confirm: 'Your wallet will ask you to sign. Nothing is sent until you do.',
  sent: 'Usually about 2 seconds.',
  confirmed: 'The deal updates by itself — no need to reload.',
  syncing: 'The deal updates by itself — no need to reload.',
};

/**
 * The step's name after "Step n of m". The wallet layer names its steps ("Create escrow", "approve
 * the amount", "confirm the payment"); a state without a name — an older caller, the `syncing`
 * phase carried over from the last step — keeps the two-prompt reading this panel was born with.
 */
export const stepLabelOf = (state: Pick<TxProgressState, 'step' | 'stepLabel'>): string =>
  state.stepLabel ?? (state.step === 1 ? 'approve the amount' : 'confirm the payment');

export const TxProgress: FC<TxProgressProps> = ({
  state,
  error,
  explorerTxUrl,
  onRetry,
  connect,
  done = false,
}) => {
  const phase = state?.phase;
  const onNetwork = !done && (phase === 'sent' || phase === 'confirmed' || phase === 'syncing');
  // Which wait the notice belongs to, rather than a boolean that has to be cleared: a new
  // transaction gets a new key and the old notice stops matching by itself.
  const waitKey = onNetwork ? `${phase}:${state?.txHash ?? ''}` : null;
  const [slowWaitKey, setSlowWaitKey] = useState<string | null>(null);
  const longWait = waitKey !== null && waitKey === slowWaitKey;

  useEffect(() => {
    if (waitKey === null) {
      return undefined;
    }

    const timer = setTimeout(() => setSlowWaitKey(waitKey), LONG_WAIT_MS);

    return () => clearTimeout(timer);
  }, [waitKey]);

  if (error) {
    return (
      <Stack gap={1} className={classNames(styles.panel, styles.error)}>
        <Typography color="white" variant="small" fontWeight="600">
          {error}
        </Typography>
        {connect ? (
          connect
        ) : onRetry ? (
          <Button title="Retry" onClick={onRetry} className={styles.retry} />
        ) : null}
      </Stack>
    );
  }

  if (!state) {
    return null;
  }

  const link = state.txHash && explorerTxUrl ? `${explorerTxUrl}${state.txHash}` : null;

  if (done) {
    return state.txHash ? (
      <Stack gap={1} className={styles.panel}>
        <Typography color="lightWhite" variant="small">
          Transaction{' '}
          {link ? (
            <a className={styles.link} href={link} target="_blank" rel="noreferrer">
              {shortenHash(state.txHash)}
            </a>
          ) : (
            shortenHash(state.txHash)
          )}
        </Typography>
      </Stack>
    ) : null;
  }

  return (
    <Stack gap={1} className={styles.panel}>
      <div className={styles.head}>
        <div className={styles.spinner} />
        <Typography color="white" variant="small" fontWeight="600">
          {PHASE_TITLE[state.phase]}
        </Typography>
      </div>

      {state.of > 1 ? (
        <Typography color="lightWhite" variant="small">
          Step {state.step} of {state.of} — {stepLabelOf(state)}
        </Typography>
      ) : null}

      <Typography color="lightWhite" variant="small">
        {PHASE_NOTE[state.phase]}
      </Typography>

      {state.txHash ? (
        <Typography color="lightWhite" variant="small">
          {link ? (
            <a className={styles.link} href={link} target="_blank" rel="noreferrer">
              {shortenHash(state.txHash)}
            </a>
          ) : (
            shortenHash(state.txHash)
          )}
        </Typography>
      ) : null}

      {longWait ? (
        <Typography color="white" variant="small">
          Taking longer than usual — your funds are safe, this will finish automatically.
        </Typography>
      ) : null}
    </Stack>
  );
};
